import React from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { Alert } from '@mui/material';

interface MissingSubscriberInfoBannerProps {
  /** CMS-1500 Box 6 value ('self' | 'spouse' | 'child' | 'other' | ''). */
  relationship?: string | null;
  /** CMS-1500 Box 4 insured first name. */
  insuredFirstName?: string | null;
  /** CMS-1500 Box 4 insured last name. */
  insuredLastName?: string | null;
  /** CMS-1500 Box 11a insured date of birth. */
  insuredDob?: string | null;
}

/**
 * Pre-submission warning for claims where the patient is a dependent
 * (Box 6 is not self) but the policyholder identity (Box 4 name / Box 11a
 * DOB) is missing. Payers reject these claims outright (GDN-2317), so we
 * surface the gap before submission instead of waiting for the rejection.
 */
export function MissingSubscriberInfoBanner({
  relationship,
  insuredFirstName,
  insuredLastName,
  insuredDob,
}: MissingSubscriberInfoBannerProps): React.ReactElement | null {
  const subjectLanguage = useSubjectLanguageVariants();

  if (!relationship || relationship === 'self') return null;

  const missingName = !insuredFirstName?.trim() || !insuredLastName?.trim();
  const missingDob = !insuredDob?.trim();
  if (!missingName && !missingDob) return null;

  const missingLabel =
    missingName && missingDob
      ? 'name and date of birth are'
      : missingName
        ? 'name is'
        : 'date of birth is';

  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid="missing-subscriber-info-banner">
      This {subjectLanguage.singular} is listed as a dependent ({relationship}), but the
      policyholder&apos;s {missingLabel} missing. The payer will reject this claim — add
      subscriber details in the {subjectLanguage.singular}&apos;s insurance profile.
    </Alert>
  );
}
